import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllMenuItems } from "../services/menu.service";
import { getOpenKitchens } from "../services/kitchen.service";
import { Kitchen, CartItem } from "../types/index"; 
import { Spinner } from "../components/common/Spinner";
import customisationImages from "../assets/customisationImages";
import CustomiseStepper from "../components/CustomiseStepper";
import { useCartStore } from "../store/cart.store";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { useLocationStore } from "../store/location.store";
import { getRoadDistance } from "../utils/distance";
import { toast } from "react-hot-toast";

// Import the component and its type
import ConcentricDial, { DialItem } from "../components/kitchen/ConcentricDial"; 
import { transformMenuToDialData } from "../utils/menuTransform";
import { Grid, Network } from "lucide-react";

// ... (Sort Icons remain same) ...
const SortDownIcon = ({ size = 18 }: { size?: number }) => (<svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5 5 5-5" /></svg>);
const SortUpIcon = ({ size = 18 }: { size?: number }) => (<svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5" /></svg>);

const ExploreMenu: React.FC = () => {
  const { coords, setPromptOpen } = useLocationStore();
  
  const { data: menuItems = [], isLoading: isMenuLoading, isError, error } = useQuery(["menuItems"], getAllMenuItems);
  const { data: kitchens = [], isLoading: isKitchensLoading } = useQuery<Kitchen[]>(["allOpenKitchens"], getOpenKitchens);

  const [search, setSearch] = useState("");
  const [selectedKitchens, setSelectedKitchens] = useState<string[]>([]);
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("price");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [viewMode, setViewMode] = useState<"grid" | "cyber">("grid");
  const [nearbyKitchenIds, setNearbyKitchenIds] = useState<string[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  // Optional, user-chosen filters. Default is "All": every available item from open kitchens.
  const [nearbyOnly, setNearbyOnly] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  useEffect(() => {
    const filterKitchens = async () => {
      if (!coords || kitchens.length === 0) { setNearbyKitchenIds([]); return; }
      setIsCalculating(true);
      try {
        const validIds: string[] = [];
        for (const kitchen of kitchens) {
          if (!kitchen.latitude || !kitchen.longitude) continue;
          const dist = await getRoadDistance(coords, { lat: kitchen.latitude, lng: kitchen.longitude });
          if (dist <= 3) { const kId = kitchen.uid || kitchen.id; if (kId) validIds.push(kId); }
        }
        setNearbyKitchenIds(validIds);
      } catch (e) { console.error(e); } finally { setIsCalculating(false); }
    };
    filterKitchens();
  }, [coords, kitchens]);

  const handleKitchenChip = (k: string) => setSelectedKitchens((prev) => prev.includes(k) ? prev.filter((val) => val !== k) : [...prev, k]);
  const handleCuisineChip = (c: string) => setSelectedCuisines((prev) => prev.includes(c) ? prev.filter((val) => val !== c) : [...prev, c]);

  const itemCategories = (item: any): string[] => {
    const cats = Array.isArray(item.categories) && item.categories.length > 0 ? item.categories : item.category ? [item.category] : [];
    return cats.filter((c: any) => typeof c === "string" && c.trim() !== "").map((c: string) => c.trim());
  };

  const visibleItems = useMemo(() => {
    // Show everything available by default; "Near me" is an optional filter.
    let items = nearbyOnly && coords
      ? menuItems.filter((item: any) => nearbyKitchenIds.includes(item.kitchenId))
      : menuItems;
    if (selectedCategory !== "All") {
      const wanted = selectedCategory.toLowerCase();
      items = items.filter((item: any) => itemCategories(item).some((c) => c.toLowerCase() === wanted));
    }
    if (search.trim() !== "") {
      const q = search.toLowerCase();
      items = items.filter((item: any) => (item.name || "").toLowerCase().includes(q) || (item.description || "").toLowerCase().includes(q));
    }
    if (selectedKitchens.length > 0) items = items.filter((item: any) => selectedKitchens.includes(item.kitchenName));
    if (selectedCuisines.length > 0) items = items.filter((item: any) => item.cuisineTags && item.cuisineTags.some((tag: string) => selectedCuisines.includes(tag)));
    
    return [...items].sort((a: any, b: any) => {
      let valA = a[sortBy]; let valB = b[sortBy];
      if (typeof valA === "string" && typeof valB === "string") { valA = valA.toLowerCase(); valB = valB.toLowerCase(); }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [menuItems, nearbyKitchenIds, search, selectedKitchens, selectedCuisines, sortBy, sortDir, coords, nearbyOnly, selectedCategory]);

  const dialData = useMemo(() => transformMenuToDialData(visibleItems), [visibleItems]);

  const kitchenOptions = useMemo(() => Array.from(new Set(menuItems.map((item: any) => item.kitchenName))), [menuItems]);
  const cuisineOptions = useMemo(() => Array.from(new Set(menuItems.map((item: any) => item.cuisineTags || []).flat().filter(Boolean))), [menuItems]);
  const categoryOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    menuItems.forEach((item: any) => itemCategories(item).forEach((c) => { const k = c.toLowerCase(); if (!byKey.has(k)) byKey.set(k, c); }));
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems]);

  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const addItem = useCartStore((state) => state.addItem);

  const handleAddToCart = (item: any) => {
    const itemToAdd: CartItem = {
      id: item.id || `${item.kitchenId}_${item.name}`,
      name: item.name,
      price: item.price,
      quantity: 1,
      category: item.category || item.cafeName,
      kitchenId: item.kitchenId || "unknown",
      kitchenName: item.kitchenName || "Unknown",
      photoUrl: item.photoUrl,
      description: item.description
    };
    addItem(itemToAdd);
  };

  const handleDialSelection = (path: DialItem[]) => {
    const selectedDialItem = path[path.length - 1];
    // ✅ FIX: TypeScript now knows .data exists
    if (selectedDialItem.data) {
        handleAddToCart(selectedDialItem.data);
        toast.success(`Added ${selectedDialItem.name} to cart!`);
    }
  };

  const handleCustomiseDone = (item: any, selections: Record<string, string[] | string>, quantity: number) => {
    const basePrice = item.price || 0;
    const itemToAdd: CartItem = {
      id: `${item.id || item.name}_${JSON.stringify(selections)}`,
      name: item.name,
      price: basePrice,
      quantity: quantity,
      category: item.category || item.cafeName,
      kitchenId: item.kitchenId || "unknown",
      kitchenName: item.kitchenName || "Unknown",
      photoUrl: item.photoUrl,
      description: item.description,
      customizations: {
        variant: selections.variant as string,
        size: selections.size as string,
        addons: selections.addons as string[],
        sides: selections.sides as string[],
        specials: selections.specials as string[],
      },
    };
    addItem(itemToAdd);
    setSelectedItem(null);
  };

  if (isMenuLoading || isKitchensLoading || (nearbyOnly && coords && isCalculating)) return <div className="p-10 flex flex-col items-center justify-center min-h-[50vh]"><Spinner size={48} /><p className="text-gray-500 mt-4">{nearbyOnly && coords ? "Finding dishes near you..." : "Loading menu..."}</p></div>;
  if (isError) return <div>Error loading menu items: {String(error)}</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {!coords && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
          <p className="text-sm text-orange-800">Set your delivery location to prioritize kitchens near you. You can still browse the full menu.</p>
          <Button className="shrink-0" onClick={() => setPromptOpen(true)}>Set Location</Button>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
         <div><h2 className="text-3xl font-bold text-gray-900">Explore Food Menu</h2><p className="text-sm text-green-600 font-medium mt-1">{nearbyOnly && coords ? "Showing items within 3km" : "Showing all available items from open kitchens"}</p></div>
         <div className="flex flex-wrap items-center gap-4">
            <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200">
                <button onClick={() => setViewMode("grid")} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === "grid" ? "bg-white text-orange-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}><Grid size={16} /> Grid</button>
                <button onClick={() => setViewMode("cyber")} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === "cyber" ? "bg-gray-900 text-cyan-400 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}><Network size={16} /> Cyber Map</button>
            </div>
            {viewMode === 'grid' && (
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm hidden sm:inline">Sort by:</span>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border p-2 rounded text-sm bg-white"><option value="price">Price</option><option value="name">Name</option></select>
                    <button onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")} className="p-2 border rounded hover:bg-gray-50 bg-white" aria-label={`Sort ${sortDir === "asc" ? "descending" : "ascending"}`}>{sortDir === "asc" ? <SortDownIcon size={18} /> : <SortUpIcon size={18} />}</button>
                </div>
            )}
         </div>
      </div>

      <div className="space-y-4 mb-8">
        <input type="text" placeholder="Search for dishes (e.g. Biryani, Pasta)..." value={search} onChange={(e) => setSearch(e.target.value)} className="border border-gray-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-orange-500 outline-none shadow-sm" />
        <div className="flex flex-col gap-3">
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-xs font-bold uppercase text-gray-400 tracking-wider mr-1">Category:</span>
              {["All", ...categoryOptions].map((c) => (
                <button key={c} onClick={() => setSelectedCategory(c)} aria-pressed={selectedCategory === c} className={`px-3 py-1 text-sm rounded-full border transition-colors capitalize ${selectedCategory === c ? "bg-orange-600 text-white border-orange-600" : "bg-white text-gray-700 border-gray-300 hover:border-orange-400"}`}>{c}</button>
              ))}
              {coords && (
                <button onClick={() => setNearbyOnly((v) => !v)} aria-pressed={nearbyOnly} className={`px-3 py-1 text-sm rounded-full border transition-colors ${nearbyOnly ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-700 border-gray-300 hover:border-green-400"}`}>Near me (3 km)</button>
              )}
            </div>
            {kitchenOptions.length > 0 && <div className="flex gap-2 flex-wrap items-center"><span className="text-xs font-bold uppercase text-gray-400 tracking-wider mr-1">Kitchens:</span>{kitchenOptions.map((k: any) => (<button key={k} onClick={() => handleKitchenChip(k)} className={`px-3 py-1 text-sm rounded-full border transition-colors ${selectedKitchens.includes(k) ? "bg-orange-600 text-white border-orange-600" : "bg-white text-gray-700 border-gray-300 hover:border-orange-400"}`}>{k}</button>))}</div>}
            {cuisineOptions.length > 0 && <div className="flex gap-2 flex-wrap items-center"><span className="text-xs font-bold uppercase text-gray-400 tracking-wider mr-1">Cuisines:</span>{cuisineOptions.map((c: any) => (<button key={c} onClick={() => handleCuisineChip(c)} className={`px-3 py-1 text-sm rounded-full border transition-colors ${selectedCuisines.includes(c) ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-700 border-gray-300 hover:border-green-400"}`}>{c}</button>))}</div>}
        </div>
      </div>

      {viewMode === 'cyber' ? (
         <div className="w-full flex justify-center mb-12 animate-in fade-in duration-500">
             {visibleItems.length > 0 ? (
                 <ConcentricDial data={dialData} onSelectionComplete={handleDialSelection} onClose={() => setViewMode('grid')} />
             ) : (
                <div className="text-center py-12 text-gray-500">No items match your filters for the Cyber Map.</div>
             )}
         </div>
      ) : (
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {visibleItems.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-200"><p className="text-gray-500 text-lg">No menu items match your filters.</p><p className="text-gray-400 text-sm">Try "All" or clear your search.</p></div>
            ) : (
            visibleItems.map((item: any) => (
                <Card key={item.id || item.name} className="flex flex-col hover:shadow-lg transition-shadow duration-200 h-full">
                <div className="relative h-48 mb-3 overflow-hidden rounded-t-lg -mx-4 -mt-4">
                    <img src={item.photoUrl || customisationImages[item.name] || "/food-hero.svg"} alt={item.name} className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8"><span className="text-white text-xs font-bold uppercase tracking-wide truncate block">{item.kitchenName}</span></div>
                </div>
                <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-1"><h3 className="font-bold text-lg text-gray-900 leading-tight">{item.name}</h3><span className="font-bold text-orange-600 ml-2 whitespace-nowrap">₹{item.price}</span></div>
                    {(item.Base || item.description) ? <p className="text-gray-500 text-sm line-clamp-2 mb-4 flex-1">{item.Base || item.description}</p> : <div className="mb-4 flex-1" />}
                    <div className="mt-auto">
                        {(item.Addons || item.Sides || item.Specials || item.Variants || item.Sizes) ? (
                        <Button variant="secondary" className="w-full text-sm" onClick={() => setSelectedItem(item)}>Customise</Button>
                        ) : (
                            <Button variant="primary" className="w-full text-sm" onClick={() => handleAddToCart(item)}>Add to Cart</Button>
                        )}
                    </div>
                </div>
                </Card>
            ))
            )}
        </div>
      )}
      {selectedItem && <CustomiseStepper item={selectedItem} onDone={(selections, quantity) => handleCustomiseDone(selectedItem, selections, quantity)} onClose={() => setSelectedItem(null)} />}
    </div>
  );
};

export default ExploreMenu;